import {blue, green, writeJson} from "@gaubee/nodekit";
import {execa} from "execa";
import path from "node:path";
import {z} from "zod";
import {projectResolver} from "../../utils/resolver.js";

const PageConfigSchema = z.object({
  model: z.string().nullable(),
  systemPrompt: z.string().nullable(),
  tools: z.array(z.any()).nullable(),
});

export interface ConfigOptions {
  sessionId: string;
  workDir: string;
}

const executeBrowserScript = async <T,>(script: string): Promise<T> => {
  const runnerPath = projectResolver("bundle/google-aistudio.browser.js");
  const {stdout} = await execa("node", [runnerPath, "--eval", script]);
  // The last line of stdout is expected to be the JSON result
  const lastLine = stdout.trim().split("\n").pop();
  if (!lastLine) {
    throw new Error("Browser script did not return a result.");
  }
  return JSON.parse(lastLine);
};

/**
 * Reads the configuration from the AI Studio page and saves it to a local file.
 * @param options - Configuration options including sessionId and workDir.
 */
export const readPageConfig = async ({sessionId, workDir}: ConfigOptions) => {
  console.log(blue(`Reading config from session: ${sessionId}...`));
  const config = await executeBrowserScript<z.infer<typeof PageConfigSchema>>(`
    const { getPageConfig } = await import('@jixo/dev/google-aistudio/browser');
    const config = await getPageConfig();
    console.log(JSON.stringify(config));
  `);

  const configPath = path.join(workDir, `${sessionId}.config.json`);
  writeJson(configPath, config);
  console.log(green(`✅ Config saved to: ${path.relative(process.cwd(), configPath)}`));
  return config;
};

/**
 * Applies a local configuration file to the AI Studio page.
 * @param options - Configuration options including sessionId and workDir.
 */
export const applyPageConfig = async ({sessionId, workDir}: ConfigOptions) => {
  const configPath = path.join(workDir, `${sessionId}.config.json`);
  console.log(blue(`Applying config from: ${path.relative(process.cwd(), configPath)}...`));

  // We pass the config path to the script to read it directly.
  // This avoids issues with large configs in command-line arguments.
  await executeBrowserScript(`
    const { setPageModel, setPageSystemPrompt, setPageFunctionCallTools } = await import('@jixo/dev/google-aistudio/browser');
    const fs = await import('node:fs/promises');
    const config = JSON.parse(await fs.readFile("${configPath.replace(/\\/g, "\\\\")}", 'utf-8'));

    if (config.model) await setPageModel(config.model);
    if (config.systemPrompt) await setPageSystemPrompt(config.systemPrompt);
    if (config.tools) await setPageFunctionCallTools(config.tools);
  `);

  console.log(green(`✅ Config successfully applied to session: ${sessionId}`));
};

/**
 * Generates a default config file in the specified working directory.
 * @param options - Configuration options including sessionId and workDir.
 */
export const genPageConfig = async ({sessionId, workDir}: ConfigOptions) => {
  const defaultConfig = {
    model: "gemini-1.5-flash-latest",
    systemPrompt: "You are a helpful assistant.",
    tools: [],
  };
  const configPath = path.join(workDir, `${sessionId}.config.json`);
  writeJson(configPath, defaultConfig);
  console.log(green(`✅ Default config generated at: ${path.relative(process.cwd(), configPath)}`));
  return defaultConfig;
};
